import {
  createContext,
  use,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { SessionUser } from "@shared/types";
import { can, canAny, type Permission } from "@shared/permissions";
import { get, post } from "@/lib/api";

interface AuthContextValue {
  user: SessionUser | null;
  /** True until the initial /api/me call settles — gate redirects on this. */
  loading: boolean;
  refresh: () => Promise<SessionUser | null>;
  logout: () => Promise<void>;
  can: (permission: Permission | Permission[]) => boolean;
  canAny: (permissions: Permission[]) => boolean;
  hasRole: (role: string) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { user: next } = await get<{ user: SessionUser | null }>("/me");
      setUser(next);
      return next;
    } catch {
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const logout = useCallback(async () => {
    await post("/auth/logout").catch(() => undefined);
    setUser(null);
    // Full reload clears any cached page state belonging to the old user.
    window.location.assign("/");
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      loading,
      refresh,
      logout,
      can: (permission) => (user ? can(user.permissions, permission) : false),
      canAny: (permissions) => (user ? canAny(user.permissions, permissions) : false),
      hasRole: (role) => user?.roles.includes(role) ?? false,
    }),
    [user, loading, refresh, logout],
  );

  return <AuthContext value={value}>{children}</AuthContext>;
}

export function useAuth(): AuthContextValue {
  const ctx = use(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
